/**
 * ContextGauge — circular context-usage indicator for the agent chat.
 *
 * Mirrors the "context window" ring in Claude Code: a small donut that fills
 * as the conversation grows. "Full" is the summarization threshold, so when
 * the ring tops out the agent auto-summarizes older messages to free space
 * (and the ring then visibly shrinks).
 *
 * The value is computed server-side (computeContextUsage) so it matches the
 * actual summarization trigger — see the backend SSE `contextUsage` event and
 * the /api/document-agent/context-usage load endpoint.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { usePortal } from "../../Portal/PortalContext";

interface ContextGaugeProps {
  /** Estimated tokens the next call will start with (baseline + history). */
  contextTokens: number;
  /** Summarization threshold — the ring is "full" here. */
  maxContextTokens: number;
}

function formatTokens(n: number): string {
  if (n >= 1000) return `${Math.round(n / 1000)}K`;
  return `${Math.max(0, Math.round(n))}`;
}

const ContextGauge: React.FC<ContextGaugeProps> = ({
  contextTokens,
  maxContextTokens,
}) => {
  const { t } = useTranslation();
  const { access } = usePortal();

  // Token budgets are a tool for staff, not something customers act on.
  if (access?.accountType === "customer") return null;
  if (!maxContextTokens || maxContextTokens <= 0) return null;

  const fraction = Math.max(0, contextTokens / maxContextTokens);
  const clamped = Math.min(1, fraction);
  const pct = Math.round(fraction * 100);

  // Ring geometry
  const size = 18;
  const stroke = 2.5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const center = size / 2;

  // Color shifts from brand-teal → amber → red as the context fills.
  let color = "#01b88e";
  if (fraction >= 0.85) color = "#e05252";
  else if (fraction >= 0.6) color = "#e6a23c";

  return (
    <span
      className="context-gauge"
      aria-label={t("documentAgentChat.contextGauge.title")}
      role="img"
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={center}
          cy={center}
          r={r}
          fill="none"
          stroke="#e3e6ea"
          strokeWidth={stroke}
        />
        <circle
          cx={center}
          cy={center}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - clamped)}
          transform={`rotate(-90 ${center} ${center})`}
          style={{
            transition: "stroke-dashoffset 0.4s ease, stroke 0.4s ease",
          }}
        />
      </svg>
      <span className="context-gauge-tooltip" role="tooltip">
        <strong>{t("documentAgentChat.contextGauge.title")}</strong>
        <br />
        {t("documentAgentChat.contextGauge.usage", {
          used: formatTokens(contextTokens),
          max: formatTokens(maxContextTokens),
          pct,
        })}
        <br />
        <span className="context-gauge-tooltip-hint">
          {t("documentAgentChat.contextGauge.hint")}
        </span>
      </span>
    </span>
  );
};

export default ContextGauge;
